"use client";

import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import * as Icons from "lucide-react";
import Link from "next/link";
import { ArrowLeft } from "lucide-react";
import type { Service } from "@/data/services";

interface Props {
  service: Service;
}

export default function ServicePageContent({ service }: Props) {
  const hasContent = (() => {
    if (!service.content) return false;
    const stripHtml = (html: string) => (html || '').replace(/<[^>]*>?/gm, '').replace(/&nbsp;/g, '').trim();
    const stripped = stripHtml(service.content);
    return stripped.length > 0 && stripped.toLowerCase() !== 'n/a';
  })();

  const containerRef = useRef<HTMLDivElement>(null);
  const backBtnRef = useRef<HTMLDivElement>(null);
  const heroCardRef = useRef<HTMLDivElement>(null);
  const heroTagsRef = useRef<HTMLDivElement>(null);
  const heroTitleRef = useRef<HTMLHeadingElement>(null);
  const heroSummaryRef = useRef<HTMLParagraphElement>(null);
  const heroDividerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const accentLineRef = useRef<HTMLDivElement>(null);
  const accentDotRef = useRef<HTMLDivElement>(null);

  const IconComp = (Icons as any)[service.iconName] || Icons.HelpCircle;
  const primaryColor = service.color || "#0d9488";

  const [backLink, setBackLink] = useState({ href: "/services", text: "Back to Services" });

  useEffect(() => {
    // Determine if it's a capability or service, and if from home
    const isFromHome = new URLSearchParams(window.location.search).get('from') === 'home';
    const isCapability = window.location.pathname.includes('/capabilities/');

    if (isFromHome) {
      setBackLink({ href: "/", text: "Back to Home" });
    } else if (isCapability) {
      // If it's a capability but not from home (e.g. direct link), we still probably want to go back to home since there is no capabilities list page
      setBackLink({ href: "/", text: "Back to Home" });
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, 50);

    const ctx = gsap.context(() => {
      // Hero Animations
      if (backBtnRef.current) gsap.fromTo(backBtnRef.current, { opacity: 0, x: -10 }, { opacity: 1, x: 0, duration: 0.5, ease: "power3.out" });
      if (heroCardRef.current) gsap.fromTo(heroCardRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7, ease: "power3.out" });
      if (heroTagsRef.current) gsap.fromTo(heroTagsRef.current, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.5, delay: 0.2, ease: "power3.out" });
      if (heroTitleRef.current) gsap.fromTo(heroTitleRef.current, { opacity: 0, y: 15 }, { opacity: 1, y: 0, duration: 0.5, delay: 0.3, ease: "power3.out" });
      if (heroSummaryRef.current) gsap.fromTo(heroSummaryRef.current, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.5, delay: 0.4, ease: "power3.out" });
      if (heroDividerRef.current) gsap.fromTo(heroDividerRef.current, { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.5, delay: 0.6, ease: "power3.out" });

      // Detailed content section
      if (contentRef.current) {
        gsap.fromTo(
          contentRef.current,
          { opacity: 0, y: 30 },
          {
            opacity: 1, y: 0, duration: 0.8, ease: "power3.out", delay: 0.4
          }
        );
      }

      // Smoothly animate the long accent line growing downwards
      if (accentLineRef.current) {
        gsap.fromTo(
          accentLineRef.current,
          { scaleY: 0, transformOrigin: "top" },
          { scaleY: 1, duration: 1.2, ease: "power3.inOut", delay: 0.8 }
        );
      }

      // Smoothly animate the solid accent indicator sliding down
      if (accentDotRef.current) {
        gsap.fromTo(
          accentDotRef.current,
          { y: -50, opacity: 0 },
          { y: 0, opacity: 1, duration: 1, ease: "bounce.out", delay: 1.2 }
        );
      }
    }, containerRef);

    return () => {
      clearTimeout(timer);
      ctx.revert();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-slate-50 font-sans selection:bg-[#0d9488]/20 selection:text-[#0d9488] pt-40 md:pt-[200px]"
    >

      {/* ─── Hero Banner Section ──────────────────────────── */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        {/* Back Button */}
        <div ref={backBtnRef} className="mb-10 ml-2 opacity-0">
          <Link href={backLink.href} className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-[#64748b] hover:text-[#0d9488] transition-colors group relative z-50 cursor-pointer py-4 pr-8 -ml-4">
            <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
            <span>{backLink.text}</span>
          </Link>
        </div>

        {/* Hero Card */}
        <div
          ref={heroCardRef}
          className="relative w-full rounded-[2rem] md:rounded-[3rem] overflow-hidden bg-white shadow-[0_20px_60px_-15px_rgba(0,0,0,0.05)] border border-slate-100 min-h-[400px] md:min-h-[500px] flex items-center opacity-0"
        >
          {/* Background Image / Ambient Gradient */}
          {service.customImage ? (
            <div className="absolute inset-0 w-full h-full pointer-events-none">
              <img src={service.customImage} alt={service.title} className="absolute inset-0 w-full h-full object-cover opacity-[0.03] mix-blend-multiply grayscale" />
              <div className="absolute inset-0 bg-gradient-to-r from-white via-white/80 to-transparent"></div>
              <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full blur-[120px] opacity-[0.06]" style={{ backgroundColor: primaryColor }} />
            </div>
          ) : (
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div
                className="absolute -top-[20%] -right-[10%] w-[600px] h-[600px] rounded-full blur-[120px] opacity-[0.06]"
                style={{ backgroundColor: primaryColor }}
              />
              <div
                className="absolute -bottom-[20%] -left-[10%] w-[400px] h-[400px] rounded-full blur-[100px] opacity-[0.04]"
                style={{ backgroundColor: primaryColor }}
              />
            </div>
          )}

          {/* Grid Pattern for Subtle Texture */}
          <div className="absolute inset-0 opacity-[0.015] pointer-events-none z-0" style={{ backgroundImage: "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCI+PHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSJub25lIiBzdHJva2U9IiMwMDAwMDAiIHN0cm9rZS13aWR0aD0iMSIvPjwvc3ZnPg==')" }} />

          <div className="relative z-10 p-8 sm:p-12 md:p-16 lg:p-20 max-w-4xl">
            <div ref={heroTagsRef} className="flex flex-wrap items-center gap-4 mb-6 opacity-0">
              <span className="flex items-center gap-2 bg-slate-50 border border-slate-200 text-slate-700 text-[10px] sm:text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full shadow-sm">
                <IconComp className="w-4 h-4" style={{ color: primaryColor }} />
                Service Overview
              </span>
            </div>

            <h1 ref={heroTitleRef} className="text-3xl sm:text-4xl md:text-5xl lg:text-[56px] font-extrabold text-slate-900 leading-[1.1] tracking-tight mb-6 opacity-0">
              {service.title}
            </h1>

            {service.description && (
              <p ref={heroSummaryRef} className="text-lg md:text-xl text-slate-600 font-medium leading-relaxed max-w-2xl mb-8 opacity-0">
                {service.description}
              </p>
            )}

            <div ref={heroDividerRef} className="w-24 h-1.5 rounded-full opacity-0 translate-y-4" style={{ backgroundColor: primaryColor }} />
          </div>
        </div>
      </div>

      {/* ─── Main Content Section ──────────────────────────── */}
      {hasContent && (
        <div className="max-w-[900px] mx-auto px-6 sm:px-8 lg:px-12 pb-12 md:pb-16 relative">
          <div
            ref={contentRef}
            className="opacity-0 relative mt-8 md:mt-12 min-h-[160px]"
          >
            {/* Creative accent element instead of a card */}
            <div ref={accentLineRef} className="absolute -left-4 md:-left-8 top-2 bottom-2 w-1.5 rounded-full opacity-30" style={{ backgroundColor: primaryColor }} />
            <div ref={accentDotRef} className="absolute -left-4 md:-left-8 top-4 w-1.5 h-16 sm:h-24 max-h-[calc(100%-2rem)] rounded-full" style={{ backgroundColor: primaryColor }} />

            <div
              className="prose prose-lg md:prose-xl max-w-none break-words whitespace-normal
                         prose-headings:font-serif prose-headings:text-slate-900 prose-headings:tracking-tight
                         prose-p:text-slate-600 prose-p:leading-relaxed prose-p:font-sans
                         prose-a:font-bold prose-a:underline hover:prose-a:opacity-80
                         prose-strong:text-slate-900 prose-strong:font-bold
                         prose-ul:text-slate-600 prose-li:text-slate-600
                         prose-blockquote:border-l-4 prose-blockquote:bg-slate-50/50 prose-blockquote:px-8 prose-blockquote:py-4 prose-blockquote:rounded-r-2xl prose-blockquote:text-slate-700 prose-blockquote:not-italic prose-blockquote:shadow-sm"
              style={{
                '--tw-prose-links': primaryColor,
                '--tw-prose-quote-borders': primaryColor,
                '--tw-prose-bullets': primaryColor,
              } as React.CSSProperties}
              dangerouslySetInnerHTML={{ __html: service.content || '' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
